import React, { useState, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PayoutActivityView from "./PayoutActivityView.component";
import ResponsiveDrawer from "../../component/sidebar/sidebar.component";
import { FontFamily } from "../../component/theme/fontFamily";
import { fontWeight } from "../../component/theme/fontWeight";
import { fontSize } from "../../component/theme/fontSize";
import {
    Box,
    Grid,
    FormControl,
    Typography,
    Paper,
    Card,
    CardContent,
    Stack,
    TextField,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import PeopleIcon from '@mui/icons-material/People';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VerifiedIcon from '@mui/icons-material/Verified';
import SchoolIcon from '@mui/icons-material/School';
import StarIcon from '@mui/icons-material/Star';
import { ThemeColor } from "../../component/theme/color";
import CustomSelect from "../../component/select field/OutlinedSelectField";
import { useDispatch, useSelector } from "react-redux";
import {
    getStudentIntakesAPI,
} from "../../Redux/service/accountService";
import { GetDashboardSummaryService } from "../../Redux/service/DashboardService";
import {
    GetUniversityAction,
    GetOrganizationAction,
} from "../../Redux/action/SettingAction";
import CustomButton from "../../component/button/button";
import { buildIntakeOptions, formatIntakeLabel } from "../../helper/intakeOptions";

// The views on this page. The grouped ones answer "where is this
// university/organisation", which is what gets asked when chasing money, so
// By University opens first.
const VIEW_STATISTICS = "statistics";
// Grouped views: "where is this university/organisation", rather than
// "where is this applicant". Each row opens its own history.
const VIEW_UNIVERSITY = "university";
const VIEW_ORGANISATION = "organisation";

// A history entry saved before By Student was removed can still say
// "tracking"; anything not on this list falls back to By University rather
// than opening on a view that no longer exists.
const KNOWN_VIEWS = [VIEW_UNIVERSITY, VIEW_ORGANISATION, VIEW_STATISTICS];

// Reusable Statistics Card Component
/**
 * One figure, with the context that makes it mean something.
 *
 * A bare "2" leaves the reader to work out two of what, out of how many.
 * `hint` answers that in words and `share` draws it, so the card can be read
 * at a glance instead of compared against the card beside it.
 */
const StatisticCard = ({
    title,
    value,
    icon: IconComponent,
    color = ThemeColor.LIGHTPURPLE,
    hint,
    share = null,
}) => (
    <Card
        sx={{
            height: "100%",
            background: `linear-gradient(135deg, #FFFFFF 0%, ${color}04 100%)`,
            border: "1px solid #E2E8F0",
            borderRadius: "16px",
            boxShadow: `0 10px 25px -5px ${color}10, 0 8px 10px -6px ${color}10`,
            position: "relative",
            overflow: "hidden",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
                boxShadow: `0 20px 25px -5px ${color}20, 0 10px 10px -5px ${color}20`,
                transform: "translateY(-4px)",
                borderColor: `${color}40`,
            },
            "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "4px",
                height: "100%",
                backgroundColor: color,
            }
        }}
    >
        <CardContent sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack spacing={1}>
                    <Typography
                        fontFamily={FontFamily.Lato}
                        fontSize="0.825rem"
                        fontWeight={700}
                        color="#64748B"
                        sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
                    >
                        {title}
                    </Typography>
                    <Typography
                        fontFamily={FontFamily.Lato}
                        fontSize="2.25rem"
                        fontWeight={800}
                        color="#1E293B"
                        sx={{ lineHeight: 1.1 }}
                    >
                        {value !== null && value !== undefined ? value : "0"}
                    </Typography>
                    {hint ? (
                        <Typography
                            fontFamily={FontFamily.Lato}
                            fontSize="0.8rem"
                            fontWeight={500}
                            color="#64748B"
                        >
                            {hint}
                        </Typography>
                    ) : null}
                </Stack>
                {IconComponent && (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "56px",
                            height: "56px",
                            borderRadius: "14px",
                            background: `linear-gradient(135deg, ${color}18 0%, ${color}08 100%)`,
                            color: color,
                            boxShadow: `0 4px 12px ${color}15`,
                        }}
                    >
                        <IconComponent sx={{ fontSize: "28px" }} />
                    </Box>
                )}
            </Stack>

            {share !== null ? (
                <Box sx={{ mt: 2.5 }}>
                    <Box
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: "#F1F5F9",
                            overflow: "hidden",
                        }}
                    >
                        <Box
                            sx={{
                                // Clamped: a share over 100 would otherwise run
                                // past the end of the track.
                                width: `${Math.min(100, Math.max(0, share))}%`,
                                height: "100%",
                                borderRadius: 3,
                                background: `linear-gradient(90deg, ${color} 0%, ${color}99 100%)`,
                                transition: "width 0.4s ease",
                            }}
                        />
                    </Box>
                </Box>
            ) : null}
        </CardContent>
    </Card>
);




/**
 * `part` as a percentage of `whole`, or null when there is nothing to divide
 * by. Returning null rather than 0 keeps "none yet" and "0%" apart - they are
 * different answers and the card words them differently.
 */
const shareOf = (part, whole) => {
    const top = Number(part) || 0;
    const bottom = Number(whole) || 0;
    if (bottom <= 0) return null;
    return Math.round((top / bottom) * 100);
};

/** "2 of 9 applications (22%)", or a plain count when there is no total. */
const outOf = (part, whole, noun) => {
    const percent = shareOf(part, whole);
    if (percent === null) return `No ${noun} recorded yet`;
    return `${part} of ${whole} ${noun} (${percent}%)`;
};

const AccountsSummaryPage = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    /**
     * Where the reader was, if they are coming back here.
     *
     * Opening View All History and pressing Back used to land on a fresh
     * page - By University, no semester - because every choice lived only in
     * this component and was gone the moment it unmounted. The choices are
     * now written onto this page's own history entry, the same way the list
     * pages keep their filters: Back returns to exactly that view, while
     * picking Summary from the sidebar opens a new entry and starts clean.
     */
    const remembered = location.state?.summaryView || {};

    // Filter state
    const [selectedSemester, setSelectedSemester] = useState(remembered.selectedSemester || "");
    const [customIntake, setCustomIntake] = useState(remembered.customIntake || "");
    const [selectedUniversity, setSelectedUniversity] = useState(remembered.selectedUniversity || "");
    const [selectedOrganization, setSelectedOrganization] = useState(remembered.selectedOrganization || "");

    // Statistics state
    const [stats, setStats] = useState({
        totalStudentsApplied: 0,
        totalApplicationsSubmitted: 0,
        totalApplicationsUniversity: 0,
        totalVisasApproved: 0,
        totalVisasApprovedUniversity: 0,
        totalStudentsOrganization: 0,
        totalApplicationsOrganization: 0,
        totalVisasOrganization: 0,
        totalVisasOrgUniversity: 0,
    });

    // Get universities and organizations from Redux
    const UniversitylistState = useSelector(
        (state) => state.setting?.university?.data?.universities
    );

    const OrganizationlistState = useSelector(
        (state) => state.setting?.organization?.data?.organizations
    );

    const [universities, setUniversities] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [semesters, setSemesters] = useState([]);
    // The grouped views' own search box and expanded row, kept so Back
    // reopens the same row rather than a collapsed list.
    const [groupedSearch, setGroupedSearch] = useState(remembered.groupedSearch || "");
    const [groupedOpenId, setGroupedOpenId] = useState(remembered.groupedOpenId || null);
    // The filters the last report was actually generated with - not what
    // the boxes say now, which may have changed since without a click.
    const reportParamsRef = useRef(remembered.reportParams || null);

    // Which view the user is looking at.
    const [activeView, setActiveView] = useState(
        KNOWN_VIEWS.includes(remembered.activeView) ? remembered.activeView : VIEW_UNIVERSITY
    );

    // Write the current view onto this history entry. `replace`, not push:
    // changing a dropdown must not add a step the reader has to Back through.
    useEffect(() => {
        const next = {
            activeView,
            selectedSemester,
            customIntake,
            selectedUniversity,
            selectedOrganization,
            groupedSearch,
            groupedOpenId,
            reportParams: reportParamsRef.current,
        };
        if (JSON.stringify(location.state?.summaryView) === JSON.stringify(next)) {
            return;
        }
        navigate(location.pathname + location.search, {
            replace: true,
            state: { ...(location.state || {}), summaryView: next },
        });
    }, [
        activeView,
        selectedSemester,
        customIntake,
        selectedUniversity,
        selectedOrganization,
        groupedSearch,
        groupedOpenId,
        navigate,
        location.pathname,
        location.search,
        location.state,
    ]);

    useEffect(() => {
        dispatch(GetUniversityAction({ page: 1, limit: 0 }));
        dispatch(GetOrganizationAction({ page: 1, limit: 0 }));
    }, [dispatch]);

    // Loads the Overall Statistics figures. The student table this used to
    // fill as well is gone, so only the metrics are asked for.
    const fetchAccountSummary = async ({ intake = "", organization = "", university = "" } = {}) => {
        setLoading(true);
        try {
            // If intake is selected, fetch metrics
            if (intake) {
                try {
                    const dashboardRes = await GetDashboardSummaryService({
                        intake,
                        universityId: university,
                        organizationId: organization
                    });
                    const dashboardData = dashboardRes.data;
                    console.log("Dashboard summary:", dashboardData);
                    if (dashboardData && dashboardData.data && dashboardData.data.metrics) {
                        const m = dashboardData.data.metrics;
                        setStats({
                            totalStudentsApplied: m.totalStudentsInIntake || 0,
                            totalApplicationsSubmitted: m.totalApplicationsSubmittedInIntake || 0,
                            totalVisasApproved: m.totalVisasApprovedInIntake || 0,
                            totalApplicationsUniversity: m.totalApplicationsInUniversity || 0,
                            totalVisasApprovedUniversity: m.totalVisasApprovedForUniversity || 0,
                            totalStudentsOrganization: m.totalStudentsFromOrganization || 0,
                            totalApplicationsOrganization: m.totalApplicationsFromOrganization || 0,
                            totalVisasOrganization: m.totalVisasFromOrganization || 0,
                            totalVisasOrgUniversity: m.totalVisasFromOrganizationForUniversity || 0,
                        });
                    }
                } catch (err) {
                    console.error("Error fetching dashboard summary:", err);
                }
            } else {
                // Clear stats if no semester selected
                setStats({
                    totalStudentsApplied: 0,
                    totalApplicationsSubmitted: 0,
                    totalApplicationsUniversity: 0,
                    totalVisasApproved: 0,
                    totalVisasApprovedUniversity: 0,
                    totalStudentsOrganization: 0,
                    totalApplicationsOrganization: 0,
                    totalVisasOrganization: 0,
                    totalVisasOrgUniversity: 0,
                });
            }
        } catch (error) {
            console.error("Error fetching account summary:", error);
        } finally {
            setLoading(false);
        }
    };

    // On arrival: the report that was on screen before, if coming back to it,
    // otherwise the unfiltered one.
    useEffect(() => {
        fetchAccountSummary(
            reportParamsRef.current || { intake: "", organization: "", university: "" }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (UniversitylistState && Array.isArray(UniversitylistState)) {
            const mapped = UniversitylistState.map(({ _id, name, ...rest }) => ({
                _id: _id,
                title: name || rest.title,
            }));
            mapped.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
            setUniversities(mapped);
        }
    }, [UniversitylistState]);

    useEffect(() => {
        if (OrganizationlistState && Array.isArray(OrganizationlistState)) {
            const mapped = OrganizationlistState.map(({ _id, organizationName, ...rest }) => ({
                _id: _id,
                title: organizationName || rest.title,
            }));
            mapped.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
            setOrganizations(mapped);
        }
    }, [OrganizationlistState]);

    // Semester options come from the shared helper so this page and the
    /**
     * The figures the cards read from, worked out once.
     *
     * `visaSuccessRate` is null rather than 0 when nothing has been submitted:
     * "no applications yet" and "0% approved" are different answers, and the
     * cards say so differently.
     */
    const visaSuccessRate = shareOf(
        stats.totalVisasApproved,
        stats.totalApplicationsSubmitted
    );

    const applicationsPerStudent = useMemo(() => {
        if (!stats.totalStudentsApplied) return "0";
        return (
            stats.totalApplicationsSubmitted / stats.totalStudentsApplied
        ).toFixed(1);
    }, [stats.totalApplicationsSubmitted, stats.totalStudentsApplied]);

    /** What the report covers, named rather than left to the filter boxes. */
    const reportScope = useMemo(() => {
        const nameOf = (list, id, fallback) => {
            const found = (list || []).find(
                (entry) => entry._id === id || entry.value === id
            );
            return found?.title || found?.label || fallback;
        };

        return [
            {
                label:
                    (selectedSemester === "custom" ? customIntake : selectedSemester) ||
                    "No semester",
                background: "#DBEAFE",
                colour: "#1D4ED8",
            },
            {
                label: selectedUniversity
                    ? nameOf(universities, selectedUniversity, "Selected university")
                    : "All universities",
                background: "#FEF3C7",
                colour: "#B45309",
            },
            {
                label: selectedOrganization
                    ? nameOf(organizations, selectedOrganization, "Selected organisation")
                    : "All organisations",
                background: "#EDE9FE",
                colour: "#6D28D9",
            },
        ];
    }, [
        selectedSemester,
        customIntake,
        selectedUniversity,
        selectedOrganization,
        universities,
        organizations,
    ]);

    /**
     * The grouped views are built from the semester alone - they ask the
     * server for every university or every organisation and group the answer.
     * Offering a university box there looked like a filter and did nothing.
     */
    const showEntityFilters = activeView === VIEW_STATISTICS;

    // By Organisation is always read for one university, so there the
    // university box comes back - and is required rather than optional.
    const showUniversityFilter =
        showEntityFilters || activeView === VIEW_ORGANISATION;
    const universityRequired = activeView === VIEW_ORGANISATION;

    // Four across with the Generate Report button, three without.
    const filterColumns = activeView === VIEW_STATISTICS ? 3 : 4;

    // Dashboard always send the same intake format to the API.
    /**
     * The semester list comes from the students themselves.
     *
     * It used to be generated - two years either side of today, Fall and
     * Spring only - so a Fall 2030 intake could not be picked at all and
     * Summer intakes were missing entirely. The generated list stays as a
     * fallback for when the call fails, so the filter is never empty.
     */
    useEffect(() => {
        let current = true;

        getStudentIntakesAPI()
            .then((res) => {
                if (!current) return;
                const stored = res.data?.data || [];
                if (!stored.length) return;
                setSemesters([
                    { _id: "custom", title: "Custom Intake..." },
                    ...stored.map((intake) => ({
                        _id: intake,
                        title: formatIntakeLabel(intake),
                    })),
                ]);
            })
            .catch(() => {});

        setSemesters([
            { _id: "custom", title: "Custom Intake..." },
            ...buildIntakeOptions()
        ]);

        return () => {
            current = false;
        };
    }, []);

    // TODO: Replace with actual API calls
    const handleSearch = async () => {
        const intakeValue = selectedSemester === "custom" ? customIntake : selectedSemester;
        const params = {
            intake: intakeValue || "",
            organization: selectedOrganization || "",
            university: selectedUniversity || "",
        };
        reportParamsRef.current = params;
        navigate(location.pathname + location.search, {
            replace: true,
            state: {
                ...(location.state || {}),
                summaryView: {
                    ...(location.state?.summaryView || {}),
                    reportParams: params,
                },
            },
        });
        await fetchAccountSummary(params);
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
            <Grid container spacing={3}>
                {/* Header Section */}
                <Grid item xs={12}>
                    <Stack spacing={1}>
                        <Typography
                            fontFamily={FontFamily.Lato}
                            fontWeight={fontWeight.W700}
                            fontSize={fontSize.h1.medium}
                            color={ThemeColor.NAVYBLUE}
                        >
                            Analytics & Reports
                        </Typography>
                        <Typography
                            fontFamily={FontFamily.Lato}
                            fontWeight={fontWeight.W400}
                            fontSize={fontSize.h4.medium}
                            color={ThemeColor.GREY}
                        >
                            View key metrics and statistics for applications and visas across semesters
                        </Typography>
                    </Stack>
                </Grid>

                {/* Filters Section */}
                <Grid item xs={12}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            background: "#FFFFFF",
                            border: "1px solid #E2E8F0",
                            borderRadius: "16px",
                            boxShadow: "0 4px 20px rgba(15, 23, 42, 0.03)",
                        }}
                    >
                        <Typography
                            fontFamily={FontFamily.Lato}
                            fontWeight={fontWeight.W600}
                            fontSize={fontSize.h3.small}
                            color={ThemeColor.NAVYBLUE}
                            sx={{ mb: 2 }}
                        >
                            Filter Statistics
                        </Typography>

                        <Grid container spacing={2} sx={{ alignItems: "flex-end" }}>
                            <Grid item xs={12} sm={6} md={filterColumns}>
                                <CustomSelect
                                    label="Select Semester *"
                                    value={selectedSemester}
                                    onChange={(e) => {
                                        setSelectedSemester(e.target.value);
                                        if (e.target.value !== "custom") {
                                            setCustomIntake("");
                                        }
                                    }}
                                    option={semesters}
                                    fullWidth
                                />
                            </Grid>

                            {selectedSemester === "custom" && (
                                <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                        label="Enter Custom Intake *"
                                        value={customIntake}
                                        onChange={(e) => setCustomIntake(e.target.value)}
                                        placeholder="e.g. summer 2026"
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            mb: 1,
                                            "& .MuiInputLabel-root": {
                                                color: "black",
                                                fontWeight: 520,
                                                fontSize: "0.9rem",
                                                fontFamily: "Lato !important"
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                height: "50px",
                                                "& .MuiOutlinedInput-notchedOutline": {
                                                    border: `0.2px solid rgb(217,217,217)`,
                                                },
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: ThemeColor.GREY,
                                                },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                    borderColor: ThemeColor.LIGHTPURPLE,
                                                },
                                            }
                                        }}
                                    />
                                </Grid>
                            )}

                            {showUniversityFilter && (
                                <Grid item xs={12} sm={6} md={filterColumns}>
                                    <CustomSelect
                                        label={
                                            universityRequired
                                                ? "Select University *"
                                                : "Select University (Optional)"
                                        }
                                        value={selectedUniversity}
                                        onChange={(e) => setSelectedUniversity(e.target.value)}
                                        option={universities}
                                        searchable={true}
                                        fullWidth
                                    />
                                </Grid>
                            )}

                            {showEntityFilters && (
                                <>
                                    <Grid item xs={12} sm={6} md={filterColumns}>
                                        <CustomSelect
                                            label="Select Organization (Optional)"
                                            value={selectedOrganization}
                                            onChange={(e) => setSelectedOrganization(e.target.value)}
                                            option={organizations}
                                            searchable={true}
                                            fullWidth
                                        />
                                    </Grid>
                                </>
                            )}

                            {/* Only Overall Statistics is generated on demand.
                                The other views load on their own, so the button
                                offered an action that did nothing there. */}
                            {activeView === VIEW_STATISTICS && (
                                <Grid item xs={12} sm={6} md={3}>
                                    <FormControl fullWidth className="mb-2" size="small">
                                        <CustomButton
                                            variant="contained"
                                            backgroundColor={ThemeColor.BLUE}
                                            color={ThemeColor.WHITE}
                                            onClick={handleSearch}
                                            loading={loading}
                                            style={{ width: "100%", height: "50px" }}
                                        >
                                            {loading ? "Loading..." : "Generate Report"}
                                        </CustomButton>
                                    </FormControl>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>
                </Grid>

                {/* View toggle. Always shown: the grouped views load on their
                    own and do not need a semester or a generated report, so
                    gating this on the student table would hide them. */}
                <Grid item xs={12}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 1,
                            display: "inline-flex",
                            background: "#FFFFFF",
                            border: "1px solid #E2E8F0",
                            borderRadius: "12px",
                            boxShadow: "0 4px 20px rgba(15, 23, 42, 0.03)",
                        }}
                    >
                        <ToggleButtonGroup
                            value={activeView}
                            exclusive
                            size="small"
                            onChange={(event, next) => {
                                // `next` is null when the active button is
                                // clicked again; keep the current view then.
                                if (next) setActiveView(next);
                            }}
                            sx={{
                                "& .MuiToggleButton-root": {
                                    fontFamily: FontFamily.Lato,
                                    fontWeight: 700,
                                    fontSize: "0.8rem",
                                    textTransform: "none",
                                    border: "none",
                                    borderRadius: "8px !important",
                                    px: 2.5,
                                    py: 1,
                                    color: "#64748B",
                                },
                                "& .Mui-selected": {
                                    bgcolor: `${ThemeColor.BLUE} !important`,
                                    color: "#FFFFFF !important",
                                },
                            }}
                        >
                            <ToggleButton value={VIEW_UNIVERSITY}>
                                By University
                            </ToggleButton>
                            <ToggleButton value={VIEW_ORGANISATION}>
                                By Organisation
                            </ToggleButton>
                            <ToggleButton value={VIEW_STATISTICS}>
                                Overall Statistics
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Paper>
                </Grid>

                {(activeView === VIEW_UNIVERSITY ||
                    activeView === VIEW_ORGANISATION) && (
                    <Grid item xs={12}>
                        <Paper
                            sx={{
                                bgcolor: "#ffffff",
                                border: "1px solid #E2E8F0",
                                boxShadow: "0 4px 20px rgba(15,23,42,0.03)",
                                borderRadius: "16px",
                                overflow: "hidden",
                            }}
                        >
                            <PayoutActivityView
                                scope={
                                    activeView === VIEW_ORGANISATION
                                        ? "organisation"
                                        : "university"
                                }
                                intake={selectedSemester === "custom" ? customIntake : selectedSemester}
                                university={
                                    activeView === VIEW_ORGANISATION ? selectedUniversity : ""
                                }
                                universityName={
                                    universities.find((uni) => uni._id === selectedUniversity)
                                        ?.title || ""
                                }
                                search={groupedSearch}
                                onSearchChange={setGroupedSearch}
                                openId={groupedOpenId}
                                onOpenChange={setGroupedOpenId}
                            />
                        </Paper>
                    </Grid>
                )}

                {/* Statistics view needs a semester */}
                {activeView === VIEW_STATISTICS && !selectedSemester && (
                    <Grid item xs={12}>
                        <Paper
                            sx={{
                                p: 4,
                                textAlign: "center",
                                border: "1px dashed #CBD5E1",
                                borderRadius: "16px",
                                background: "#FFFFFF",
                            }}
                        >
                            <Typography
                                fontFamily={FontFamily.Lato}
                                fontSize={fontSize.h3.small}
                                color={ThemeColor.GREY}
                            >
                                Select a semester and click Generate Report to see the overall statistics.
                            </Typography>
                        </Paper>
                    </Grid>
                )}

                {/* Statistics Cards */}
                {activeView === VIEW_STATISTICS && selectedSemester && (
                    <>
                        {/* What these figures cover. Without it the numbers
                            float free of the filters that produced them, and a
                            screenshot of this page means nothing a week later. */}
                        <Grid item xs={12}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    mb: 1,
                                    borderRadius: "16px",
                                    border: "1px solid #E2E8F0",
                                    background: `linear-gradient(135deg, ${ThemeColor.BLUE}0A 0%, #FFFFFF 60%)`,
                                }}
                            >
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    spacing={2}
                                >
                                    <Stack spacing={1.25}>
                                        <Typography
                                            fontFamily={FontFamily.Lato}
                                            fontWeight={fontWeight.W700}
                                            fontSize={fontSize.h3.medium}
                                            color={ThemeColor.NAVYBLUE}
                                        >
                                            Overall Statistics
                                        </Typography>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            flexWrap="wrap"
                                            rowGap={1}
                                        >
                                            {reportScope.map((chip) => (
                                                <Chip
                                                    key={chip.label}
                                                    size="small"
                                                    label={chip.label}
                                                    sx={{
                                                        bgcolor: chip.background,
                                                        color: chip.colour,
                                                        fontWeight: 700,
                                                        fontSize: "0.72rem",
                                                        borderRadius: "6px",
                                                    }}
                                                />
                                            ))}
                                        </Stack>
                                    </Stack>

                                    <Stack
                                        alignItems={{ xs: "flex-start", md: "flex-end" }}
                                        spacing={0.5}
                                    >
                                        <Typography
                                            fontFamily={FontFamily.Lato}
                                            fontSize="0.78rem"
                                            fontWeight={700}
                                            color="#64748B"
                                            sx={{
                                                textTransform: "uppercase",
                                                letterSpacing: "0.5px",
                                            }}
                                        >
                                            Visa success rate
                                        </Typography>
                                        <Typography
                                            fontFamily={FontFamily.Lato}
                                            fontSize="2rem"
                                            fontWeight={800}
                                            color={
                                                visaSuccessRate === null
                                                    ? "#94A3B8"
                                                    : "#10B981"
                                            }
                                            sx={{ lineHeight: 1 }}
                                        >
                                            {visaSuccessRate === null
                                                ? "—"
                                                : `${visaSuccessRate}%`}
                                        </Typography>
                                        <Typography
                                            fontFamily={FontFamily.Lato}
                                            fontSize="0.78rem"
                                            color="#64748B"
                                        >
                                            {visaSuccessRate === null
                                                ? "No applications submitted yet"
                                                : `${stats.totalVisasApproved} approved of ${stats.totalApplicationsSubmitted} submitted`}
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <StatisticCard
                                title="Total Students Applied"
                                value={stats.totalStudentsApplied}
                                icon={PeopleIcon}
                                color={ThemeColor.BLUE}
                                hint={
                                    stats.totalStudentsApplied > 0
                                        ? `${applicationsPerStudent} applications each on average`
                                        : "No students in this semester yet"
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <StatisticCard
                                title="Total Applications Submitted"
                                value={stats.totalApplicationsSubmitted}
                                icon={AssignmentIcon}
                                color={ThemeColor.LIGHTPURPLE}
                                hint={
                                    stats.totalApplicationsSubmitted > 0
                                        ? `From ${stats.totalStudentsApplied} student${
                                              stats.totalStudentsApplied === 1 ? "" : "s"
                                          }`
                                        : "Nothing submitted yet"
                                }
                            />
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <StatisticCard
                                title="Total Visas Approved"
                                value={stats.totalVisasApproved}
                                icon={VerifiedIcon}
                                color="#10B981"
                                hint={outOf(
                                    stats.totalVisasApproved,
                                    stats.totalApplicationsSubmitted,
                                    "applications"
                                )}
                                share={visaSuccessRate}
                            />
                        </Grid>

                        {/* Section 2: University-specific Statistics */}
                        {selectedUniversity && (
                            <>
                                <Grid item xs={12}>
                                    <Stack spacing={1} sx={{ mb: 2 }}>
                                        <Typography
                                            fontFamily={FontFamily.Lato}
                                            fontWeight={fontWeight.W600}
                                            fontSize={fontSize.h3.medium}
                                            color={ThemeColor.NAVYBLUE}
                                        >
                                            University-Specific Statistics
                                        </Typography>
                                        <Box
                                            sx={{
                                                height: "3px",
                                                width: "50px",
                                                background: `linear-gradient(90deg, ${ThemeColor.ORANGE} 0%, ${ThemeColor.LIGHTBLUE} 100%)`,
                                                borderRadius: "2px",
                                            }}
                                        />
                                    </Stack>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <StatisticCard
                                        title="Applications in Selected University"
                                        value={stats.totalApplicationsUniversity}
                                        icon={SchoolIcon}
                                        color={ThemeColor.ORANGE}
                                        hint={outOf(
                                            stats.totalApplicationsUniversity,
                                            stats.totalApplicationsSubmitted,
                                            "applications this semester"
                                        )}
                                        share={shareOf(
                                            stats.totalApplicationsUniversity,
                                            stats.totalApplicationsSubmitted
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <StatisticCard
                                        title="Visas Approved for Selected University"
                                        value={stats.totalVisasApprovedUniversity}
                                        icon={VerifiedIcon}
                                        color="#ff9800"
                                        hint={outOf(
                                            stats.totalVisasApprovedUniversity,
                                            stats.totalApplicationsUniversity,
                                            "applications here"
                                        )}
                                        share={shareOf(
                                            stats.totalVisasApprovedUniversity,
                                            stats.totalApplicationsUniversity
                                        )}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Section 3: Organization-specific Statistics */}
                        {selectedOrganization && (
                            <>
                                <Grid item xs={12}>
                                    <Stack spacing={1} sx={{ mb: 2 }}>
                                        <Typography
                                            fontFamily={FontFamily.Lato}
                                            fontWeight={fontWeight.W600}
                                            fontSize={fontSize.h3.medium}
                                            color={ThemeColor.NAVYBLUE}
                                        >
                                            Organization-Specific Statistics
                                        </Typography>
                                        <Box
                                            sx={{
                                                height: "3px",
                                                width: "50px",
                                                background: `linear-gradient(90deg, ${ThemeColor.PURPLE} 0%, ${ThemeColor.RED} 100%)`,
                                                borderRadius: "2px",
                                            }}
                                        />
                                    </Stack>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <StatisticCard
                                        title="Total Students from Organization"
                                        value={stats.totalStudentsOrganization}
                                        icon={PeopleIcon}
                                        color={ThemeColor.PURPLE}
                                        hint={outOf(
                                            stats.totalStudentsOrganization,
                                            stats.totalStudentsApplied,
                                            "students this semester"
                                        )}
                                        share={shareOf(
                                            stats.totalStudentsOrganization,
                                            stats.totalStudentsApplied
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <StatisticCard
                                        title="Applications from Organization"
                                        value={stats.totalApplicationsOrganization}
                                        icon={AssignmentIcon}
                                        color="#9c27b0"
                                        hint={outOf(
                                            stats.totalApplicationsOrganization,
                                            stats.totalApplicationsSubmitted,
                                            "applications this semester"
                                        )}
                                        share={shareOf(
                                            stats.totalApplicationsOrganization,
                                            stats.totalApplicationsSubmitted
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <StatisticCard
                                        title="Visas from Organization"
                                        value={stats.totalVisasOrganization}
                                        icon={VerifiedIcon}
                                        color="#673ab7"
                                        hint={outOf(
                                            stats.totalVisasOrganization,
                                            stats.totalApplicationsOrganization,
                                            "of its applications"
                                        )}
                                        share={shareOf(
                                            stats.totalVisasOrganization,
                                            stats.totalApplicationsOrganization
                                        )}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Section 4: Combined Organization & University Statistics */}
                        {selectedOrganization && selectedUniversity && (
                            <>
                                <Grid item xs={12}>
                                    <Stack spacing={1} sx={{ mb: 2 }}>
                                        <Typography
                                            fontFamily={FontFamily.Lato}
                                            fontWeight={fontWeight.W600}
                                            fontSize={fontSize.h3.medium}
                                            color={ThemeColor.NAVYBLUE}
                                        >
                                            Combined Organization & University Statistics
                                        </Typography>
                                        <Box
                                            sx={{
                                                height: "3px",
                                                width: "50px",
                                                background: `linear-gradient(90deg, ${ThemeColor.RED} 0%, ${ThemeColor.BLUE} 100%)`,
                                                borderRadius: "2px",
                                            }}
                                        />
                                    </Stack>
                                </Grid>

                                <Grid item xs={12} sm={6} md={4}>
                                    <StatisticCard
                                        title="Visas from Organization for Selected University"
                                        value={stats.totalVisasOrgUniversity}
                                        icon={StarIcon}
                                        color={ThemeColor.RED}
                                        hint={outOf(
                                            stats.totalVisasOrgUniversity,
                                            stats.totalVisasOrganization,
                                            "of this organisation's visas"
                                        )}
                                        share={shareOf(
                                            stats.totalVisasOrgUniversity,
                                            stats.totalVisasOrganization
                                        )}
                                    />
                                </Grid>
                            </>
                        )}

                        {/* Empty state when no data yet */}
                        {Object.values(stats).every((val) => val === 0) && (
                            <Grid item xs={12}>
                                <Paper
                                    sx={{
                                        p: 4,
                                        textAlign: "center",
                                        background: `linear-gradient(135deg, ${ThemeColor.LIGHTGREY}20 0%, transparent 100%)`,
                                    }}
                                >
                                    <Typography
                                        fontFamily={FontFamily.Lato}
                                        fontSize={fontSize.h3.small}
                                        color={ThemeColor.GREY}
                                    >
                                        No data available for the selected filters. Statistics will appear here after applying filters.
                                    </Typography>
                                </Paper>
                            </Grid>
                        )}
                    </>
                )}

            </Grid>
        </Box>
    );
};

export default function AccountsSummaryWrapper() {
    return <ResponsiveDrawer maincontent={<AccountsSummaryPage />} />;
}
